import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Tag, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import CTASection from "../components/CTASection";
import { renderMarkdown } from "../lib/markdown";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", content: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPost();
    loadComments();
  }, [slug]);

  const loadPost = async () => {
    try {
      const { data } = await api.get(`/blog/${slug}`);
      setPost(data);
      document.title = `${data.title} | Pool Guru Blog`;
      const all = await api.get("/blog");
      setRelated(all.data.filter((p) => p.slug !== slug).slice(0, 3));
    } catch {
      setPost(null);
    }
  };

  const loadComments = async () => {
    try {
      const { data } = await api.get(`/blog/${slug}/comments`);
      setComments(data);
    } catch {}
  };

  const submitComment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/blog/${slug}/comments`, { ...form, post_slug: slug });
      toast.success("Comment submitted for review!");
      setForm({ name: "", email: "", content: "" });
    } catch (e) {
      toast.error("Please fill in all fields correctly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!post) {
    return (
      <div className="section-container py-20 text-center">
        <h1 className="text-2xl font-bold mb-4">Post not found</h1>
        <Link to="/blog" className="btn-primary">← Back to blog</Link>
      </div>
    );
  }

  return (
    <div data-testid={`blog-post-${slug}`}>
      <section className="bg-gradient-to-b from-pink-50/50 to-white py-16">
        <div className="section-container max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-brand-blue font-semibold mb-6 hover:text-brand-blue-hover" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <span className="px-3 py-1 bg-brand-pink text-white text-xs font-bold rounded-full">
              {post.category}
            </span>
            <span className="text-sm text-slate-500 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.created_at).toLocaleDateString()}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-6">{post.title}</h1>
          <p className="text-xl text-slate-600">{post.excerpt}</p>
        </div>
      </section>

      {post.featured_image && (
        <div className="section-container max-w-4xl">
          <div className="aspect-[16/8] rounded-2xl overflow-hidden shadow-xl">
            <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <section className="py-16">
        <div className="section-container max-w-3xl">
          <div className="prose-content text-lg">{renderMarkdown(post.content)}</div>

          {post.tags.length > 0 && (
            <div className="mt-10 pt-8 border-t border-slate-200">
              <div className="overline mb-3">Tags</div>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-semibold rounded-full">
                    <Tag className="w-3 h-3 inline mr-1" />{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* COMMENTS */}
          <div className="mt-16">
            <h2 className="text-3xl font-extrabold mb-8">Comments</h2>
            {comments.length > 0 ? (
              <div className="space-y-6 mb-10">
                {comments.map((c) => (
                  <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-bold text-brand-navy">{c.name}</div>
                      <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</div>
                    </div>
                    <p className="text-slate-700">{c.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 mb-10">Be the first to leave a comment!</p>
            )}

            <form onSubmit={submitComment} className="bg-slate-50 border border-slate-200 rounded-2xl p-8" data-testid="comment-form">
              <h3 className="text-xl font-bold mb-4">Leave a comment</h3>
              <p className="text-sm text-slate-500 mb-6">Comments are reviewed before publishing.</p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Input
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="comment-name"
                />
                <Input
                  type="email"
                  placeholder="Your email (not published)"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="comment-email"
                />
              </div>
              <Textarea
                placeholder="Your comment..."
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={4}
                required
                className="mb-4"
                data-testid="comment-content"
              />
              <Button type="submit" disabled={submitting} className="bg-brand-pink hover:bg-brand-pink-hover" data-testid="comment-submit">
                {submitting ? "Submitting..." : "Post Comment"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="section-container">
            <h2 className="text-3xl font-extrabold mb-8">Related articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group bg-white rounded-xl border border-slate-200 p-6 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                  data-testid={`related-${r.slug}`}
                >
                  <div className="text-xs font-bold text-brand-pink mb-2">{r.category}</div>
                  <h3 className="text-lg font-bold group-hover:text-brand-pink transition-colors line-clamp-2">{r.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
